import React from 'react';
import { DeviceType } from '@wix/cashier-common/dist/src/enums/DeviceType';
import { myWalletPageStartedLoading, myWalletPageFinishedLoading } from '@wix/bi-logger-cashier-ugc/v2';
import pick from 'lodash.pick';
import { generateGUID } from '@wix/cashier-common/dist/src/utils/guid/generateGUID';
import { demoViewModeKey } from '../../types/DemoViewMode';
import { FreeTextKey } from '../../types/FreeText';
import { InteractionName } from '../../types/InteractionName';
import { getBiLogger, updateBiDefaults } from '../../utils/biLogger';
import { fedopsLogger } from '../../utils/fedopsLogger';
import { wixSdkAdapter } from '../../utils/wixSdkAdapter';
import { EditorEventPublicDataChange, EditorEventSettingUpdate } from '../../types/EditorEvents';
import { Locale } from '../../types/Locale';
import { AppConnect } from './view.connect';
import { connector, AppControllerPropsFromRedux } from './controller.connector';

export interface IAppControllerProps extends AppControllerPropsFromRedux {
  deviceType: DeviceType;
  locale: string;
}

class AppController extends React.Component<IAppControllerProps> {
  private firstLoad = true;
  private reloadOnNav = false;

  componentDidMount() {
    const { viewMode, fetchCards, fetchFreeText, fetchDemoCards, fetchRecurringPaymentMethods } = this.props;

    fedopsLogger.interactionStarted(InteractionName.APP_MOUNT);

    this.initBILogger();
    getBiLogger().report(myWalletPageStartedLoading({}));

    const isSiteViewMode = viewMode === 'site';

    fetchFreeText();
    fetchRecurringPaymentMethods();

    if (isSiteViewMode) {
      fetchCards();
    } else {
      fetchDemoCards();
    }

    wixSdkAdapter.Wix.addEventListener(wixSdkAdapter.Wix.Events.PAGE_NAVIGATION_IN, (payload) => {
      if (!payload.wasAppOnPage) {
        this.reloadOnNav = true;
        getBiLogger().report(myWalletPageStartedLoading({}));
        fedopsLogger.interactionStarted(InteractionName.APP_NAVIGATION_IN);
        fetchCards();
      }
    });

    if (!isSiteViewMode) {
      wixSdkAdapter.Wix.addEventListener(
        wixSdkAdapter.Wix.Events.PUBLIC_DATA_CHANGED,
        this.handleSettingsOrPublicDataUpdate,
      );
    }
  }

  private initBILogger() {
    /**
     * Wallet appSessionId should be unique every render.
     * So there is not sense to store it in localStorage
     */
    const appSessionId = generateGUID();
    this.props.setAppSessionId(appSessionId);
    updateBiDefaults({ appSessionId });
  }

  componentDidUpdate(prevProps) {
    if (this.props.deviceType !== prevProps.deviceType) {
      this.props.setDeviceType(this.props.deviceType);
    }

    if (this.props.locale !== prevProps.locale) {
      this.props.setLocale(this.props.locale as unknown as Locale);
    }

    if (!prevProps.isAppLoaded && this.props.isAppLoaded) {
      wixSdkAdapter.Wix.Performance.applicationLoaded();

      getBiLogger().report(
        myWalletPageFinishedLoading({
          hasSavedCreditCards: this.props.cardsLength,
        }),
      );

      if (this.firstLoad) {
        fedopsLogger.appLoaded();
        fedopsLogger.interactionEnded(InteractionName.APP_MOUNT);
        this.firstLoad = false;
      }

      if (this.reloadOnNav) {
        fedopsLogger.interactionEnded(InteractionName.APP_NAVIGATION_IN);
        this.reloadOnNav = false;
      }
    }
  }

  componentWillUnmount() {
    const { viewMode } = this.props;

    if (viewMode !== 'site') {
      wixSdkAdapter.Wix.removeEventListener(
        wixSdkAdapter.Wix.Events.PUBLIC_DATA_CHANGED,
        this.handleSettingsOrPublicDataUpdate,
      );
    }
  }

  handleSettingsOrPublicDataUpdate = async (payload: EditorEventSettingUpdate | EditorEventPublicDataChange) => {
    const { setFreeText, setDemoViewMode } = this.props;

    const updatedData = payload?.details || payload;
    const freeText = pick(updatedData, Object.values(FreeTextKey));

    if (Object.keys(freeText).length) {
      setFreeText(freeText);
    }

    if (updatedData.hasOwnProperty(demoViewModeKey)) {
      setDemoViewMode(updatedData[demoViewModeKey]);
    }
  };

  render() {
    return <AppConnect />;
  }
}

export const AppControllerConnect = connector(AppController);
